import React from 'react'
import {
  ArrowBackIcon,
  EditIcon,
  WarningOutlineIcon
} from '@toasttab/buffet-pui-icons'
import { FormProvider } from 'react-hook-form'
import { ButtonType } from 'src/apollo/sites'
import {
  formatDeliveryAddressLine1,
  formatDeliveryAddressLine2
} from 'src/public/components/online_ordering/addressUtils'
import InputField from 'src/shared/components/common/form_input/InputField'
import LoadingSpinnerOverlay from 'src/shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { t } from 'src/translations/src'
import { BrandedButton } from 'src/shared/components/common/button'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { getCountryAddressConfig } from 'src/public/components/online_ordering/address_configs'
import { useOptionalRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { AddressDetailsScreenProps } from '../types'

/**
 * AddressDetailsScreen - Shared component for editing/confirming address details
 */
const AddressDetailsScreen = ({
  address,
  closeModal,
  backOrEditButtonHandler,
  confirmHandler,
  formMethods,
  allowBackNav,
  loading = false,
  errorMsg = '',
  entryPoint = 'edit'
}: AddressDetailsScreenProps) => {
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const ooRestaurant = useOptionalRestaurant()?.ooRestaurant
  const addressConfig = getCountryAddressConfig(ooRestaurant?.i18n?.country)

  return (
    <div data-testid='address-confirm-screen' className='addressConfirmScreen'>
      <div className='modalHeader'>
        {allowBackNav && (
          <button
            data-testid='confirm-back-btn'
            aria-label={t('fulfillment-general.back')}
            type='button'
            className='backArrow'
            onClick={backOrEditButtonHandler}
          >
            <ArrowBackIcon />
          </button>
        )}
        <h2>
          {entryPoint === 'edit'
            ? t('address-edit-screen.title')
            : t('address-confirm-screen.title')}
        </h2>
      </div>
      {errorMsg && (
        <div className='warningBanner' data-testid='address-search-error'>
          <WarningOutlineIcon size='xs' accessibility='decorative' />
          <span>{errorMsg}</span>
        </div>
      )}
      <div className='modalContent'>
        <FormProvider {...formMethods}>
          <div className='confirmContainer'>
            <div className='selectedAddress'>
              <div className='addressText'>
                <div className='addrLine1'>
                  {intlAddressCountryLogicErrorHandling && address
                    ? addressConfig.formatters.formatLine1(
                        address?.deliveryInfo
                      )
                    : formatDeliveryAddressLine1(address?.deliveryInfo, false)}
                </div>
                <div className='addrLine2'>
                  {intlAddressCountryLogicErrorHandling && address
                    ? addressConfig.formatters.formatLine2(
                        address?.deliveryInfo
                      )
                    : formatDeliveryAddressLine2(address?.deliveryInfo, true)}
                </div>
              </div>
              <button
                type='button'
                className='editAddressButton'
                onClick={backOrEditButtonHandler}
                aria-label={t('address-confirm-screen.edit-address-aria')}
              >
                <EditIcon />
              </button>
            </div>
            {entryPoint !== 'fulfillment' && (
              <div className='addressInputField'>
                <p>{t('address-edit-screen.address-label-name')}</p>
                <InputField
                  data-testid='address-label-input'
                  id='intermediateAddressLabel'
                  type='text'
                  label={t('address-edit-screen.address-label')}
                  preventFormSubmit
                />
              </div>
            )}
            <div className='addressInputField'>
              <p>{t('address-confirm-screen.additional-details-label')}</p>
              <InputField
                data-testid='address-2-input'
                id='intermediateAddress2'
                type='text'
                label={t('address-confirm-screen.apartment-unit-optional')}
                preventFormSubmit
              />
            </div>
            <div
              data-testid='deliveryInstructions'
              className='addressInputField'
            >
              <p>{t('address-confirm-screen.delivery-instructions-label')}</p>
              <InputField
                data-testid='delivery-instructions-input'
                id='intermediateDeliveryInstructions'
                type='text'
                label={t('address-confirm-screen.delivery-instructions')}
                preventFormSubmit
              />
            </div>
          </div>
        </FormProvider>
      </div>
      <div className='footer'>
        <BrandedButton
          onClick={closeModal}
          className='cancelButton'
          data-testid='address-cancel'
        >
          {t('fulfillment-general.cancel')}
        </BrandedButton>
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={confirmHandler}
          data-testid='address-confirm'
        >
          {entryPoint === 'edit'
            ? t('address-edit-screen.button.save')
            : t('address-confirm-screen.confirm-address')}
        </BrandedButton>
      </div>
      {loading && <LoadingSpinnerOverlay withSubtleBorderRadius />}
    </div>
  )
}

export default AddressDetailsScreen
